import React, { useState, Dispatch, SetStateAction, useContext, useMemo } from 'react'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { ReadyState } from 'react-use-websocket'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { MiwaAppWebsocketContext } from '@/contexts/miwa-app-websocket'
import { TMiwaLockStatus, TMiwaAppWebsocketContext, TMiwaState } from '@/models/miwa-lock'
import { MiwaLockStatus } from '@/constants/miwa-lock'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { TimePicker } from '@/components/atoms/time-picker'
import moment from 'moment'
dayjs.extend(isSameOrBefore)

type TKeyCardWriteModalProps = {
  miwaState: TMiwaState
  setMiwaState: Dispatch<SetStateAction<TMiwaState>>
  onClickModalCancel: () => void
  onClickModalSave: (type: TMiwaLockStatus) => void
}

const MINIMUM_ROOM_CARD = 1
const MAXIMUM_ROOM_CARD = 99

export const KeyCardWriteModal: React.FC<TKeyCardWriteModalProps> = ({
  miwaState,
  setMiwaState,
  onClickModalCancel,
  onClickModalSave,
}) => {
  const { t } = useTranslation()

  const { checkinTime, checkinDate, checkoutTime, checkoutDate, numberOfCards, lastCheckinDatetime } = miwaState
  const [numberCards, setNumberCards] = useState(numberOfCards ? String(numberOfCards) : '')
  const isNewIssue = miwaState.issueType === MiwaLockStatus.ISSUE
  // 再発行・追加発行は発行済みカードの実際の開始日時を引き継ぐため、予約のチェックイン日時ではなく最終発行日時を表示する
  const startAt = isNewIssue ? dayjs(`${checkinDate} ${checkinTime}`) : dayjs(lastCheckinDatetime)
  const { readyState } = useContext<TMiwaAppWebsocketContext>(MiwaAppWebsocketContext)

  const [isDisabledButton, setIsDisableButton] = useState(false)

  const onChangeRoomCard = (e: React.ChangeEvent<HTMLInputElement>) => {
    const result = e.target.value.replace(/[^0-9]/g, '').replace(/^0(\d)/, '$1')
    if (Number(result) <= MAXIMUM_ROOM_CARD) {
      setNumberCards(result)
      setMiwaState({ ...miwaState, numberOfCards: Number(result) })
    }
  }

  const onClickWriteKeyCard = (type: TMiwaLockStatus) => {
    if (!_validate()) return

    onClickModalSave(type)
  }

  const _validate = (): boolean => {
    const errorMessage = t('Checkout time before checkin time')
    // validate checkinTime & checkoutTime empty
    if (!miwaState.checkinTime || !miwaState.checkoutTime) {
      window.alert(errorMessage)
      return false
    }
    // validate checkoutTime greater than start time
    if (checkoutTime && dayjs(`${checkoutDate} ${checkoutTime}`).isSameOrBefore(startAt)) {
      window.alert(errorMessage)
      return false
    }

    return true
  }

  useMemo(() => {
    if (!numberOfCards || numberOfCards < MINIMUM_ROOM_CARD || numberOfCards > MAXIMUM_ROOM_CARD || readyState !== ReadyState.OPEN) {
      setIsDisableButton(true)
    } else {
      setIsDisableButton(false)
    }
  }, [numberOfCards, readyState])

  return (
    <Modal customCss={{ width: 680, height: 'unset', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
      <ModalHeader>{t('Issue card')}</ModalHeader>
      <ModalBody>
        <div css={bodyWrapperStyle}>
          <div>
            <div css={keyCardWriteTimeDate}>
              <div css={keyCardWriteTimeDateWrapper}>
                <div css={keyCardWriteTime}>{startAt.isValid() ? startAt.format(t('MM-DD')) : '-'}</div>
                {isNewIssue ? (
                  <div css={keyCardWriteCheckinTime}>
                    <TimePicker
                      minuteStep={15}
                      value={checkinTime ? (moment(checkinTime, 'HH:mm') as any) : undefined}
                      onChange={value => setMiwaState({ ...miwaState, checkinTime: value?.format('HH:mm') })}
                      suffixIcon={<img src={require('@/static/images/select_gray.svg')} />}
                    />
                  </div>
                ) : (
                  <div>{startAt.isValid() ? startAt.format('HH:mm') : '-'}</div>
                )}
              </div>
              <div css={keyCardWriteTimeSpace}>~</div>
              <div css={keyCardWriteTimeDateWrapper}>
                <div css={keyCardWriteTime}>{miwaState.checkoutDate ? dayjs(miwaState.checkoutDate).format(t('MM-DD')) : '-'}</div>
                <div css={keyCardWriteCheckinTime}>
                  <TimePicker
                    minuteStep={60}
                    value={checkoutTime ? (moment(checkoutTime, 'HH:mm') as any) : undefined}
                    onChange={value => setMiwaState({ ...miwaState, checkoutTime: value?.format('HH:mm') })}
                    suffixIcon={<img src={require('@/static/images/select_gray.svg')} />}
                  />
                </div>
              </div>
            </div>
            <div css={keyCardWriteType}>
              <span>
                {miwaState.typeNameJa ?? '-'}／{miwaState.roomNumber ?? '-'}
              </span>
            </div>
          </div>
          <div>
            <div>
              <label htmlFor="roomCard" css={keyCardWriteInputLabel}>
                {t('Number card')}
              </label>
              <input
                type="text"
                name="roomCard"
                placeholder="0"
                minLength={MINIMUM_ROOM_CARD}
                maxLength={MAXIMUM_ROOM_CARD}
                css={keyCardWriteInput}
                value={numberCards}
                onChange={onChangeRoomCard}
              />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div css={footerButtonWrapperStyle}>
          <div css={footerButtonCancelStyle}>
            <Button width={110} height={38} buttonType={3} onClick={onClickModalCancel}>
              {t('Cancel')}
            </Button>
          </div>

          <div css={footerButtonSaveStyle}>
            {isNewIssue ? (
              <Button
                width={109}
                height={38}
                buttonType={isDisabledButton ? 5 : 1}
                onClick={() => onClickWriteKeyCard(MiwaLockStatus.ISSUE)}
                isDisabled={isDisabledButton}
              >
                {t('Issue new')}
              </Button>
            ) : (
              <>
                <Button
                  width={93}
                  height={38}
                  buttonType={isDisabledButton ? 5 : 3}
                  marginRight={10}
                  onClick={() => onClickWriteKeyCard(MiwaLockStatus.REISSUE)}
                  isDisabled={isDisabledButton}
                >
                  {t('Issue again')}
                </Button>

                <Button
                  width={109}
                  height={38}
                  buttonType={isDisabledButton ? 5 : 1}
                  onClick={() => onClickWriteKeyCard(MiwaLockStatus.ADDNEWISSUE)}
                  isDisabled={isDisabledButton}
                >
                  {t('Additional Issue')}
                </Button>
              </>
            )}
          </div>
        </div>
      </ModalFooter>
    </Modal>
  )
}

const bodyWrapperStyle = css({
  width: '100%',
  background: '#F2F2F2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const keyCardWriteTimeDate = css({
  display: 'flex',
  alignItems: 'center',
  color: '#272727',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.7px',
})

const keyCardWriteTimeDateWrapper = css({
  display: 'flex',
  alignItems: 'center',
})

const keyCardWriteTime = css({
  fontWeight: 500,
})

const keyCardWriteCheckinTime = css({
  '> span': {
    marginLeft: 6,
    width: 60,
    input: {
      border: 'none',
      background: 'rgba(0, 0, 0, 0)',
      fontSize: 14,
      fontWeight: 500,
      color: '#272727',
      paddingLeft: 0,
      paddingRight: 0,
    },
    span: {
      marginRight: 0,
      right: 0,
    },
  },
})

const keyCardWriteTimeSpace = css({
  fontWeight: 500,
  display: 'inline-block',
  margin: '0 16px',
})

const keyCardWriteType = css({
  color: '#272727',
  fontSize: '19px',
  fontWeight: 'bold',
  lineHeight: '51px',
  letterSpacing: '0.95px',
})

const keyCardWriteInputLabel = css({
  color: '#676767',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '21px',
  letterSpacing: '0.6px',
  marginRight: '16px',
})

const keyCardWriteInput = css({
  color: '#272727',
  fontsize: '14px',
  letterSpacing: '0.7px',
  lineHeight: '20px',
  '&::placeholder': {
    color: '#ccc',
  },
  padding: '9px 16px',
  width: '64px',
  height: '32px',
  border: '1px solid #CCCCCC',
  borderRadius: '16px',
})

const footerButtonWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  backgroundColor: 'white',
})

const footerButtonCancelStyle = css({})

const footerButtonSaveStyle = css({
  display: 'flex',
  alignItems: 'center',
})
